import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Checkbox, Select, Spin } from "antd";
import { PropertyState } from "PhishingManager/pages/Step3/Step3.types";
import InfinityScrollWrapper from "Components/InfinityScroll/InfinityScrollWrapper";
import { debounce } from "lodash";

interface Props {
    loading: boolean;
    propertyData: PropertyState;
    selectedProperties: number[];
    setSelectedProperties: (properties: number[]) => void;
    allSelected: boolean;
    setAllSelected: (val: boolean) => void;
    onPropertyDeselect: (propertyValue: number) => void;
    setSearchValue: (val: string | null) => void;
    moveCurrentPage: () => void;
}

const PropertySelect: React.FC<Props> = ({
    loading,
    propertyData,
    selectedProperties,
    allSelected,
    setSelectedProperties,
    setAllSelected,
    onPropertyDeselect,
    setSearchValue,
    moveCurrentPage
}) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [search, setSearch] = useState("");

    const handleSelectAll = useCallback(() => {
        if (allSelected) {
            setAllSelected(false);
            setSelectedProperties([]);
        } else {
            setAllSelected(true);
            setSelectedProperties(propertyData.data.map((i) => i.value));
            setSearchValue(null);
        }
    }, [allSelected, propertyData.data]);

    const dropdownRender = useCallback(() => (
        <>
            <div className="group-recipients-all-option">
                <div className="group-recipients-option">
                    <Checkbox checked={allSelected} onClick={handleSelectAll}>
                        Select All
                    </Checkbox>
                </div>
            </div>

            {propertyData.data.length === 0 ? (
                <div className="no-item-dropdown">No properties found</div>
            ) : (
                <Spin spinning={loading}>
                    <InfinityScrollWrapper
                        hasMore={propertyData.data.length < propertyData.totalPages}
                        dataLength={propertyData.data.length}
                        onLoadMore={() => {
                            if (!loading && propertyData.data.length) moveCurrentPage();
                        }}
                        loading={!propertyData.search && loading}
                        height={200}
                        scrollableId="property-select-dropdown"
                        loaderText="Loading properties..."
                    >
                        {propertyData.data.map((el, index) => (
                            <div className="group-recipients-option" key={`${el.value}-${index}`}>
                                <Checkbox
                                    onClick={() => {
                                        if (selectedProperties.includes(el.value)) {
                                            setSelectedProperties(selectedProperties.filter(v => v !== el.value));
                                            onPropertyDeselect(el.value);
                                        } else {
                                            setSelectedProperties([...selectedProperties, el.value]);
                                        }
                                    }}
                                    disabled={allSelected}
                                    checked={allSelected || selectedProperties.includes(el.value)}
                                >
                                    {el.label}
                                </Checkbox>
                            </div>
                        ))}
                    </InfinityScrollWrapper>
                </Spin>
            )}
        </>
    ), [dropdownOpen, loading, propertyData, allSelected, selectedProperties, handleSelectAll]);

    useEffect(() => {
        setDropdownOpen(!loading);
    }, [loading])

    const debouncedSetSearch = useMemo(() => debounce((value: string) => setSearchValue(value), 500), []);

    useEffect(() => {
        return () => debouncedSetSearch.cancel();
    }, [debouncedSetSearch]);

    return (
        <Select
            className="phishing-step3__properties_select"
            mode="multiple"
            style={{ width: "100%" }}
            options={propertyData.data}
            placeholder="Select option"
            size="large"
            maxTagCount={0}
            showSearch={!allSelected}
            tagRender={() => <span />}
            maxTagPlaceholder={() => (
                <span>
                    {allSelected ? "All Properties" : `${selectedProperties.length} selected`}
                </span>
            )}
            value={selectedProperties}
            onSearch={(val) => {
                setSearch(val);
                debouncedSetSearch(val)
            }}
            onDropdownVisibleChange={(open) => {
                setDropdownOpen(open);
                if (!open) setSearchValue(null);
            }}
            getPopupContainer={(trigger) => trigger.parentElement}
            dropdownRender={dropdownRender}
        />
    );
};

export default PropertySelect;